import type { FC, ReactElement } from "react";

import { Card } from "@jti/core-components/components/Card";
import type { OrderItem as CartItem } from "@jti/magento";
import { useTranslations } from "@jti/ui";

import { useCheckoutSubscriptionDisplayState } from "@checkout/hooks";
import getCartItemsCount from "@checkout/utils/getCartItemsCount";

import { SummarySubscriptionFooter } from "./SummarySubscriptionFooter/SummarySubscriptionFooter";
import SummarySubscriptionItem from "./SummarySubscriptionItem/SummarySubscriptionItem";

type SummaryCartItemsProps = {
  items: CartItem[];
  nonCollapsibleContent?: ReactElement;
  isCollapsible?: boolean;
  showProductList?: boolean;
};

export const SummarySubscription: FC<SummaryCartItemsProps> = ({
  items,
  nonCollapsibleContent,
  isCollapsible = true,
  showProductList = true,
}) => {
  const { __ } = useTranslations();
  const { showSidebarBillingDisclaimer } = useCheckoutSubscriptionDisplayState(items);

  const summaryFooter = nonCollapsibleContent || (
    <SummarySubscriptionFooter
      displayPointsSection={false}
      showSidebarBillingDisclaimer={showSidebarBillingDisclaimer}
    />
  );

  if (!items.length) {
    return null;
  }

  const itemsCount = getCartItemsCount(items);

  const collapsibleProps = isCollapsible ? ({ isCollapsible: true } as const) : ({} as const);

  return (
    <Card
      variant="level-2"
      title={__("summarySubscription.title", [itemsCount])}
      data-testid="SummaryCartSubscription"
      {...collapsibleProps}
    >
      {showProductList ? (
        <ul>
          {items.map((item) => (
            <SummarySubscriptionItem {...item} key={item.sku} />
          ))}
        </ul>
      ) : null}
      {summaryFooter}
    </Card>
  );
};
